import React, { useState, useEffect, useRef } from "react";
import { FiSend } from "react-icons/fi";
import { IoCheckmark } from "react-icons/io5";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchConversationsAction,
  sendMessageAction,
  toggleAdminConversationsAction,
} from "../../store/conversation/conversationThunk";

const categories = ["Foreclosure", "FSBO", "Expired", "Probate", "Buyer"];

const ChatbotUI = ({
  username,
  avatar,
  messages,
  onSendMessage,
  selectedChat,
}) => {
  const [userMessage, setUserMessage] = useState("");
  // console.log(selectedChat, "selectedChat in chatbot UI");
  const chatboxRef = useRef(null);
  const conversations = useSelector(
    (state) => state.conversations.conversations
  );
  // console.log("Full conversations state:", conversations);
  // console.log("Selected chat:", selectedChat);
  // console.log("LeadAssignmentId:", selectedChat?.LeadAssignmentId);
  // console.log("Conversations for this lead:", conversations[selectedChat?.LeadAssignmentId]);

  const currentConversation = selectedChat?.chatId ? conversations[selectedChat?.chatId]?.[0] : null;
  // console.log("Current conversation:", currentConversation);
  // console.log("Conversation type:", currentConversation?.type);

  // Disable input for user type conversations, enable for bot type
  const isInputEnabled = currentConversation?.type === "user";
  // console.log("isInputEnabled:", isInputEnabled);

  const dispatch = useDispatch();

  useEffect(() => {
    if (chatboxRef.current) {
      chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
    }
  }, [messages]);

  const handleChat = () => {
    if (!userMessage.trim()) return;

    const newMessage = {
      type: "AI",
      content: userMessage.trim(),
    };

    if (selectedChat && selectedChat.LeadAssignmentId) {
      dispatch(
        sendMessageAction({
          LeadAssignmentId: selectedChat.LeadAssignmentId,
          Message: userMessage.trim(),
          chatId: selectedChat.chatId, // Pass chatId for proper conversation refresh
        })
      );
    }

    onSendMessage(newMessage);
    setUserMessage("");
  };

  const handleToggle = () => {
    if (selectedChat && selectedChat.LeadAssignmentId) {
      dispatch(
        toggleAdminConversationsAction({
          LeadAssignmentId: selectedChat.LeadAssignmentId,
        })
      ).unwrap()
        .then(() => {
          console.log("Conversation toggled successfully");
          // Use chatId instead of LeadAssignmentId for fetchConversationsAction
          if (selectedChat.chatId) {
            dispatch(fetchConversationsAction({ chatId: selectedChat.chatId }));
          }
        })
        .catch((error) => {
          console.error("Error toggling conversation:", error);
        });
    }
  };
  const [selectedCategory, setSelectedCategory] = useState(null);

  return (
    <div className="chatbot-container h-fit">
      <div className="chatbot-window">
        <div className="chatbot-header">
          {username && (
            <div className="flex items-center mt-2">
              <img
                src={avatar}
                alt={username}
                className="w-8 h-8 rounded-full mr-2"
              />
              <span className="text-lg font-semibold">{username}</span>
            </div>
          )}
        </div>
        <div className=" w-full border-b-2 bg-[#e8e2fa] shadow">
          <div className="flex flex-col lg:flex-row text-lg font-semibold gap-3 justify-center items-center py-3">
            {categories.map((category, index) => (
              <span
                key={index}
                onClick={() => setSelectedCategory(category)}
                className={`rounded-full text-md p-1 px-4 cursor-pointer shadow ${
                  selectedCategory === category
                    ? "bg-[#724ae8] text-white"
                    : "bg-white"
                }`}
              >
                {category}
              </span>
            ))}
          </div>
        </div>
        <ul className="chatbox custom-scrollbar" ref={chatboxRef}>
          {messages.map((msg, index) => {
            const isUserMessage = msg.type.toLowerCase() === "user";
            const isAIMessage = msg.type.toLowerCase() === "ai";
            const messageBgColor = isUserMessage ? "bg-red-500" : "bg-blue-500";
            const justifyClass = isUserMessage
              ? "justify-start"
              : "justify-end";

            return (
              <li
                key={index}
                className={`chat flex items-center mb-2 ${justifyClass}`}
              >
                {isUserMessage && (
                  <img
                    src={avatar}
                    alt={username}
                    className="w-8 h-8 rounded-full mr-2"
                  />
                )}
                <p
                  className={`chat-message ${messageBgColor} text-white p-2 rounded-lg flex items-end justify-between`}
                >
                  <span>{msg.content}</span>
                  {/* Show tick mark for user messages (blue background ones) */}
                  {!isUserMessage && (
                    <IoCheckmark className="text-white/70 w-3 h-3 ml-2 flex-shrink-0" />
                  )}
                </p>
              </li>
            );
          })}
        </ul>
        <div className="chat-input-container flex items-center p-2 border-t border-gray-300">
          <label className="switch">
            <input
              type="checkbox"
              checked={isInputEnabled}
              onChange={handleToggle}
              aria-label="Enable or disable message input"
            />
            <span className="slider round"></span>
          </label>
          <textarea
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            placeholder="Enter a message..."
            className="chat-input flex-grow p-2 border rounded-lg mr-2"
            rows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleChat();
              }
            }}
            disabled={!isInputEnabled}
            aria-label="Chat message input"
          />
          <button
            onClick={handleChat}
            className={`send-button text-white p-2 rounded-full ${
              isInputEnabled
                ? "bg-blue-500"
                : "!bg-gray-500 !cursor-not-allowed"
            }`}
            disabled={!isInputEnabled}
            aria-label="Send message"
          >
            <FiSend size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

ChatbotUI.propTypes = {
  username: PropTypes.string.isRequired,
  avatar: PropTypes.string.isRequired,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
    })
  ).isRequired,
  onSendMessage: PropTypes.func.isRequired,
  selectedChat: PropTypes.object.isRequired, // Add selectedChat prop
};

export default ChatbotUI;
